import React, { useState } from "react";
import PropTypes from "prop-types";
import { formatCount, isWeekend, money, weekdayOf } from "./incomeLabels";

// What each kiosk brought on each day of a day by day report (GET
// /api/monitoring/income/daily): a row a kiosk, the most income first, and a column a
// day, so a kiosk more is a row more and a month of days fits a screen. A day is a
// colored cell, deeper the more it brought, or its number in the other view.
// The color compares a kiosk's days with each other, so a small kiosk shows its good
// and bad days as a large one does; the totals beside the rows compare the kiosks.
// It can compare all the kiosks' days instead.

// Five steps of one hue, further from the card's surface the more a day brought:
// darker on white, lighter on gray-800. The text of each is checked for contrast on it.
const STEPS = [
  "bg-purple-200 text-gray-900 dark:bg-purple-900 dark:text-white",
  "bg-purple-300 text-gray-900 dark:bg-purple-600 dark:text-white",
  "bg-purple-400 text-gray-900 dark:bg-purple-400 dark:text-gray-900",
  "bg-purple-600 text-white dark:bg-purple-300 dark:text-gray-900",
  "bg-purple-800 text-white dark:bg-purple-100 dark:text-gray-900",
];
// A day without income keeps its place in the grid, as an empty box
const IDLE = "border border-gray-200 dark:border-gray-600";

// What a day's color is measured against: the best day of its own kiosk, or of any.
// Among all the kiosks the steps go by the square root, so the days of a small kiosk
// are not all the first step beside those of a large one.
const SCALES = [
  { key: "kiosk", label: "Киоск бүрээр", note: "киоскийн өөрийн хамгийн өндөр өдрөөс", curve: (share) => share },
  { key: "all", label: "Бүгдээр", note: "бүх киоскийн хамгийн өндөр өдрөөс", curve: Math.sqrt },
];

// A day's step among STEPS, by its share of the best day
const stepOf = (share, scale) =>
  Math.min(STEPS.length - 1, Math.floor(scale.curve(share) * STEPS.length));
// The largest share of the best day in a step, as "40%"
const limitOf = (step, scale) => {
  const shares = [...Array(101).keys()].filter((percent) => stepOf(percent / 100, scale) <= step);
  return `${shares[shares.length - 1]}%`;
};

const nameOf = (kiosk) => kiosk.name || "Нэргүй киоск";

// A kiosk's part of all the income, as "12%"
const shareOf = (income, total) => {
  if (total <= 0) return "—";
  const percent = Math.round((income / total) * 100);
  return percent === 0 && income > 0 ? "<1%" : `${percent}%`;
};

// The months of the days, each with how many of the days are its
const monthsOf = (days) =>
  days.reduce((months, day) => {
    const month = day.slice(0, 7);
    const last = months[months.length - 1];
    if (last && last.month === month) last.count += 1;
    else months.push({ month, count: 1 });
    return months;
  }, []);

// The table as a file Excel opens: the mark in front tells it the names are UTF-8
const BOM = String.fromCharCode(0xfeff);
const csvOf = (days, kiosks, totals) => {
  const cell = (value) => `"${String(value).replace(/"/g, '""')}"`;
  const rows = [
    ["Киоск", "Байршил", ...days, "Нийт"],
    ...kiosks.map((kiosk) => [nameOf(kiosk), kiosk.location || "", ...kiosk.days, kiosk.income]),
    ["Нийт", "", ...totals, totals.reduce((sum, value) => sum + value, 0)],
  ];
  return BOM + rows.map((row) => row.map(cell).join(",")).join("\r\n");
};

const save = (text, name) => {
  const url = URL.createObjectURL(new Blob([text], { type: "text/csv;charset=utf-8" }));
  const link = document.createElement("a");
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
};

const VIEWS = [
  { key: "heat", label: "Өнгөөр" },
  { key: "numbers", label: "Тоогоор" },
];

// The kiosk and the total stay in view while the days scroll sideways; on a phone
// only the kiosk does, or nothing would be left for the days
const surface = "bg-white dark:bg-gray-800";
const kioskCell = `sticky left-0 z-10 border-r border-gray-100 dark:border-gray-700 ${surface}`;
const totalCell = `sm:sticky sm:right-0 border-l border-gray-100 dark:border-gray-700 ${surface}`;

const DailyIncomeHeatmap = ({ days, kiosks, totals, selected, onSelect }) => {
  const [view, setView] = useState("heat");
  const [scaleKey, setScaleKey] = useState(SCALES[0].key);
  // The cell under the pointer, and where its tooltip goes
  const [tip, setTip] = useState(null);
  const numbers = view === "numbers";

  const scale = SCALES.find((option) => option.key === scaleKey);
  // The best day of each kiosk, and of them all
  const bests = kiosks.map((kiosk) => Math.max(...kiosk.days));
  const best = Math.max(0, ...bests);
  const top = Math.max(0, ...kiosks.map((kiosk) => kiosk.income));
  const total = totals.reduce((sum, value) => sum + value, 0);

  const showTip = (event) => {
    const cell = event.target.closest("[data-cell]");
    if (!cell) return setTip(null);
    const [row, column] = cell.dataset.cell.split("-").map(Number);
    const box = cell.getBoundingClientRect();
    const center = box.left + box.width / 2;
    setTip({
      row,
      column,
      left: Math.min(Math.max(center, 120), window.innerWidth - 120),
      top: box.top - 6,
    });
  };

  const dayCell = (kiosk, row, column) => {
    const value = kiosk.days[column];
    const hovered = tip && tip.row === row && tip.column === column;
    const most = scale.key === "kiosk" ? bests[row] : best;
    const fill = value > 0 ? STEPS[stepOf(value / most, scale)] : numbers ? "" : IDLE;
    return (
      <td
        key={days[column]}
        className={"p-px " + (selected && selected !== String(kiosk.id) ? "opacity-40" : "")}
      >
        <div
          data-cell={`${row}-${column}`}
          style={numbers ? undefined : { minWidth: 20 }}
          className={
            (numbers ? "px-2 py-1.5 text-sm text-right tabular-nums " : "h-8 ") +
            `rounded ${fill} ` +
            (hovered ? "ring-2 ring-gray-800 dark:ring-white" : "")
          }
        >
          {numbers ? (
            value ? (
              formatCount(value)
            ) : (
              <span className="text-gray-300 dark:text-gray-600">—</span>
            )
          ) : (
            <span className="sr-only">{formatCount(value)}</span>
          )}
        </div>
      </td>
    );
  };

  const tipKiosk = tip && kiosks[tip.row];

  return (
    <section className="overflow-hidden bg-white border border-gray-100 rounded-xl shadow-xs dark:bg-gray-800 dark:border-gray-700">
      <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-700">
        <div>
          <h2 className="text-base font-semibold text-gray-800 dark:text-gray-100">Киоскоор</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Мөр бүр нэг киоск, багана бүр нэг өдөр · киоск дээр дарвал зөвхөн түүнийг харуулна
          </p>
        </div>
        {kiosks.length > 0 && (
          <div className="flex items-center gap-3">
            <div className="inline-flex h-9 p-1 bg-gray-100 rounded-lg dark:bg-gray-700">
              {VIEWS.map((option) => (
                <button
                  type="button"
                  key={option.key}
                  onClick={() => setView(option.key)}
                  aria-pressed={view === option.key}
                  className={`px-3 text-sm font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                    view === option.key
                      ? "text-purple-700 bg-white shadow-sm dark:text-purple-300 dark:bg-gray-800"
                      : "text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() =>
                save(csvOf(days, kiosks, totals), `orlogo_${days[0]}_${days[days.length - 1]}.csv`)
              }
              className="h-9 px-3 text-sm font-medium text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-300 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              CSV татах
            </button>
          </div>
        )}
      </div>

      {kiosks.length === 0 ? (
        <p className="px-5 py-10 text-sm text-center text-gray-500 dark:text-gray-400">
          Сонгосон хугацаанд орлого бүртгэгдээгүй байна.
        </p>
      ) : (
        <>
          <div className="w-full overflow-x-auto" onMouseLeave={() => setTip(null)}>
            {/* border-separate: a collapsed border would scroll away from a sticky cell */}
            <table
              className="w-full border-separate whitespace-nowrap"
              style={{ borderSpacing: 0 }}
              onMouseOver={showTip}
            >
              <thead className="text-xs text-gray-500 dark:text-gray-400">
                <tr>
                  <th className={"pt-3 " + kioskCell} />
                  {monthsOf(days).map(({ month, count }) => (
                    <th
                      key={month}
                      colSpan={count}
                      className="px-1 pt-3 font-semibold text-left tabular-nums"
                    >
                      {/* A month of a day or two has no room for its year */}
                      {numbers || count > 2 ? month : month.slice(5)}
                    </th>
                  ))}
                  <th className={"pt-3 " + totalCell} />
                </tr>
                <tr>
                  <th className={"py-2 pl-5 pr-3 font-semibold tracking-wide text-left uppercase " + kioskCell}>
                    Киоск
                  </th>
                  {days.map((day) => (
                    <th key={day} className="px-px py-2 font-normal text-center">
                      <span
                        className={
                          "block " +
                          (isWeekend(day)
                            ? "font-semibold text-gray-700 dark:text-gray-200"
                            : "text-gray-400 dark:text-gray-500")
                        }
                      >
                        {weekdayOf(day)}
                      </span>
                      <span className="block tabular-nums">{Number(day.slice(8))}</span>
                    </th>
                  ))}
                  <th className={"py-2 pl-4 pr-5 font-semibold tracking-wide text-right uppercase align-bottom " + totalCell}>
                    Нийт
                  </th>
                </tr>
              </thead>
              <tbody>
                {kiosks.map((kiosk, row) => {
                  const id = String(kiosk.id);
                  const active = selected === id;
                  return (
                    <tr key={id}>
                      <th scope="row" className={"py-1 pl-5 pr-3 font-normal text-left " + kioskCell}>
                        {active && (
                          <span
                            className="absolute left-0 w-1 bg-purple-600 rounded-tr-lg rounded-br-lg inset-y-1"
                            aria-hidden="true"
                          ></span>
                        )}
                        <button
                          type="button"
                          onClick={() => onSelect(active ? "" : id)}
                          aria-pressed={active}
                          title={`${nameOf(kiosk)} · ${kiosk.location || "Байршилгүй"}`}
                          className="block w-28 text-left rounded sm:w-52 focus:outline-none focus:ring-2 focus:ring-purple-300"
                        >
                          <span
                            className={
                              "block text-sm font-medium truncate " +
                              (active
                                ? "text-purple-700 dark:text-purple-300"
                                : "text-gray-800 dark:text-gray-100")
                            }
                          >
                            {nameOf(kiosk)}
                          </span>
                          <span className="block text-xs text-gray-500 truncate dark:text-gray-400">
                            {kiosk.location || "Байршилгүй"}
                          </span>
                        </button>
                      </th>
                      {kiosk.days.map((value, column) => dayCell(kiosk, row, column))}
                      <td className={"py-1 pl-4 pr-5 text-right " + totalCell}>
                        <span className="block text-sm font-semibold text-gray-900 tabular-nums dark:text-white">
                          {money(kiosk.income)}
                        </span>
                        {/* Against the kiosk that brought the most; the number is its share of all */}
                        <span className="flex items-center justify-end mt-1">
                          <span className="block w-16 h-1.5 bg-gray-100 rounded-full dark:bg-gray-700">
                            <span
                              className="block h-1.5 bg-purple-600 rounded-full dark:bg-purple-500"
                              style={{ width: `${top > 0 ? Math.max(kiosk.income / top, 0) * 100 : 0}%` }}
                            />
                          </span>
                          <span className="w-10 text-xs text-gray-500 tabular-nums dark:text-gray-400">
                            {shareOf(kiosk.income, total)}
                          </span>
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              {/* The colored view has no room for a day's total; the chart above shows them */}
              {numbers && (
                <tfoot>
                  <tr className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                    <th scope="row" className={"py-3 pl-5 pr-3 text-left border-t " + kioskCell}>
                      Нийт
                    </th>
                    {totals.map((value, column) => (
                      <td
                        key={days[column]}
                        className="py-3 pl-2 text-right border-t border-gray-100 tabular-nums dark:border-gray-700"
                        style={{ paddingRight: 9 }}
                      >
                        {formatCount(value)}
                      </td>
                    ))}
                    <td className={"py-3 pl-4 pr-5 text-right text-purple-700 border-t tabular-nums dark:text-purple-300 " + totalCell}>
                      {money(total)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>

          {/* What the colors mean, beside what they are measured against */}
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 px-5 py-3 text-xs text-gray-500 border-t border-gray-100 dark:text-gray-400 dark:border-gray-700">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <span>Өнгийг харьцуулах:</span>
              <div className="inline-flex h-8 p-1 bg-gray-100 rounded-lg dark:bg-gray-700">
                {SCALES.map((option) => (
                  <button
                    type="button"
                    key={option.key}
                    onClick={() => setScaleKey(option.key)}
                    aria-pressed={scale === option}
                    className={`px-3 font-medium rounded-md transition-colors focus:outline-none focus:ring-2 focus:ring-purple-300 ${
                      scale === option
                        ? "text-purple-700 bg-white shadow-sm dark:text-purple-300 dark:bg-gray-800"
                        : "text-gray-600 hover:text-gray-800 dark:text-gray-300 dark:hover:text-gray-100"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-1">
              <span className={"w-4 h-4 rounded " + IDLE} />
              <span className="mr-3">Орлогогүй</span>
              <span className="mr-1">Бага</span>
              {STEPS.map((step, index) => (
                <span
                  key={step}
                  title={`${scale.note} ${limitOf(index, scale)} хүртэл`}
                  className={"w-4 h-4 rounded " + step}
                />
              ))}
              <span className="ml-1">Их</span>
            </div>
          </div>
        </>
      )}

      {tip && tipKiosk && (
        <div
          className="fixed z-50 px-3 py-2 text-xs text-white bg-gray-900 rounded-lg shadow-lg pointer-events-none dark:text-gray-900 dark:bg-gray-100"
          style={{ left: tip.left, top: tip.top, transform: "translate(-50%, -100%)" }}
        >
          <p className="text-sm font-semibold tabular-nums">{money(tipKiosk.days[tip.column])}</p>
          <p>
            {nameOf(tipKiosk)} · {days[tip.column]} {weekdayOf(days[tip.column])}
          </p>
        </div>
      )}
    </section>
  );
};

DailyIncomeHeatmap.propTypes = {
  days: PropTypes.array.isRequired,
  kiosks: PropTypes.array.isRequired,
  // What all the kiosks brought on each of the days
  totals: PropTypes.array.isRequired,
  // The id of the kiosk the page shows alone, or "" for all of them
  selected: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
};

export default DailyIncomeHeatmap;
